import type { BaseExcelColor } from './excelColor';
import type { Vector2 } from '@/utils/vec';
import z from 'zod';

export const unitSchema = z.union([
  z.number(), // pixels
  z.literal('auto'),
]);
export type Unit = z.infer<typeof unitSchema>;

export type Style = {
  [key: string]: string | number | boolean | Vector2 | BaseExcelColor | Style;
};

export type BaseStyle<T extends Style> = {
  [key in keyof T]?: T[key] | null;
};

export type StoredStyle<T extends Style> = Readonly<BaseStyle<T>>;

export type StyleToMerge<T extends Style> = {
  [key in keyof T]?: T[key] | null | 'unset';
};

export function mergeStyles<S extends Style>(
  base: BaseStyle<S>,
  other: StyleToMerge<S>,
): BaseStyle<S> {
  const result: BaseStyle<S> = {};
  const keys = new Set(Object.keys(base).concat(Object.keys(other))) as Set<
    keyof S
  >;
  for (const key of keys) {
    if (other[key] === 'unset') continue;
    if (other[key] !== undefined) {
      result[key] = other[key] as S[keyof S];
      continue;
    }
    result[key] = base[key];
  }
  return result;
}

/**
 * A null on a stored style means to use the base style
 */
export function computeStyle<S extends Style>(
  base: S,
  ...styles: (BaseStyle<S> | undefined | null)[]
): S {
  const result: S = { ...base };
  for (const style of styles) {
    for (const key in style) {
      if (style[key] === null) {
        result[key] = base[key];
      } else if (style[key] !== undefined) {
        result[key] = style[key];
      }
    }
  }
  return result;
}
